/**
 * Retry utility for Supabase queries and async operations.
 * Implements exponential back-off with jitter to avoid thundering herd.
 */

export interface RetryOptions {
  maxAttempts?: number;
  baseDelayMs?: number;
  maxDelayMs?: number;
  onRetry?: (attempt: number, error: Error) => void;
}

const DEFAULT_OPTIONS: Required<RetryOptions> = {
  maxAttempts: 3,
  baseDelayMs: 500,
  maxDelayMs: 5000,
  onRetry: () => {},
};

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function calcDelay(attempt: number, baseDelayMs: number, maxDelayMs: number): number {
  // Exponential back-off: base * 2^attempt + jitter
  const exp = Math.min(baseDelayMs * Math.pow(2, attempt), maxDelayMs);
  const jitter = Math.random() * 200;
  return exp + jitter;
}

/**
 * Retries an async function up to `maxAttempts` times with exponential back-off.
 * Throws the last error if all attempts fail.
 */
export async function retryQuery<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  let lastError: Error = new Error('Unknown error');

  for (let attempt = 0; attempt < opts.maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (err: any) {
      lastError = err instanceof Error ? err : new Error(String(err));
      if (attempt < opts.maxAttempts - 1) {
        opts.onRetry(attempt + 1, lastError);
        const delay = calcDelay(attempt, opts.baseDelayMs, opts.maxDelayMs);
        await sleep(delay);
      }
    }
  }

  throw lastError;
}

/**
 * Wraps a Supabase query result (which returns { data, error }) with retry logic.
 * Throws if the Supabase response contains an error.
 */
export async function retrySupabaseQuery<T>(
  fn: () => Promise<{ data: T | null; error: any }>,
  options: RetryOptions = {}
): Promise<T> {
  return retryQuery(async () => {
    const { data, error } = await fn();
    if (error) throw new Error(error.message || 'Supabase query failed');
    return data as T;
  }, options);
}
